import React, { useState, useEffect, useCallback } from 'react';
import { 
  FileText, Search, Edit2, Save, X, ExternalLink, Globe, 
  RefreshCw, Plus, Trash2, AlertCircle, CheckCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { useAdmin } from '../../context/AdminContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminMetadataPage = () => {
  const { token } = useAdmin();
  const [metadata, setMetadata] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingRoute, setEditingRoute] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newRouteForm, setNewRouteForm] = useState({ route: '' });

  const fetchMetadata = useCallback(async () => {
    try {
      setLoading(true);
      const params = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : '';
      const response = await axios.get(`${API}/api/admin/metadata${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMetadata(response.data.metadata || []);
    } catch (err) {
      console.error('Failed to fetch metadata:', err);
      toast.error('Failed to load page metadata');
    } finally {
      setLoading(false);
    }
  }, [token, searchQuery]);

  useEffect(() => {
    fetchMetadata();
  }, [fetchMetadata]);

  const handleEdit = (item) => {
    setEditingRoute(item.route);
    setEditForm({
      title: item.title || '',
      meta_description: item.meta_description || '',
      og_title: item.og_title || '',
      og_description: item.og_description || '',
      og_image_url: item.og_image_url || '',
      canonical_url: item.canonical_url || '',
      keywords: item.keywords || ''
    });
  };

  const handleCancelEdit = () => {
    setEditingRoute(null);
    setEditForm({});
  };

  const handleSave = async (route) => {
    try {
      setSaving(true);
      const cleanRoute = route.replace(/^\//, '');
      await axios.put(
        `${API}/api/admin/metadata/${cleanRoute}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Metadata updated for ${route}`);
      setEditingRoute(null);
      setEditForm({});
      fetchMetadata();
    } catch (err) {
      console.error('Failed to save metadata:', err);
      toast.error('Failed to save metadata');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (route) => {
    if (!window.confirm(`Are you sure you want to delete metadata for ${route}?`)) {
      return;
    }
    
    try {
      const cleanRoute = route.replace(/^\//, '');
      await axios.delete(
        `${API}/api/admin/metadata/${cleanRoute}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Metadata deleted for ${route}`);
      fetchMetadata();
    } catch (err) {
      console.error('Failed to delete metadata:', err);
      toast.error('Failed to delete metadata');
    }
  };

  const handleAddRoute = async () => {
    if (!newRouteForm.route) {
      toast.error('Route is required');
      return;
    }

    try {
      setSaving(true);
      await axios.post(
        `${API}/api/admin/metadata`,
        { route: newRouteForm.route },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Metadata created for ${newRouteForm.route}`);
      setShowAddModal(false);
      setNewRouteForm({ route: '' });
      fetchMetadata();
    } catch (err) {
      console.error('Failed to create metadata:', err);
      toast.error(err.response?.data?.detail || 'Failed to create metadata');
    } finally {
      setSaving(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchMetadata();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">SEO / Page Metadata</h1>
          <p className="text-slate-500 mt-1">
            Manage SEO metadata for all public-facing pages
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={fetchMetadata}
            disabled={loading}
            data-testid="refresh-metadata"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-trust-blue hover:bg-trust-blue-hover text-white"
            data-testid="add-route-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Route
          </Button>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by route or title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="metadata-search"
          />
        </div>
        <Button type="submit" variant="outline">
          Search
        </Button>
      </form>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{metadata.length}</p>
              <p className="text-sm text-slate-500">Total Pages</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {metadata.filter(m => m.title && m.meta_description).length}
              </p>
              <p className="text-sm text-slate-500">Complete</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {metadata.filter(m => !m.title || !m.meta_description).length}
              </p>
              <p className="text-sm text-slate-500">Incomplete</p>
            </div>
          </div>
        </div>
      </div>

      {/* Metadata List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Route</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Title</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Description</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Status</th>
                <th className="text-right px-4 py-3 text-sm font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                    Loading metadata...
                  </td>
                </tr>
              ) : metadata.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                    No metadata found
                  </td>
                </tr>
              ) : (
                metadata.map((item) => (
                  <React.Fragment key={item.route}>
                    {editingRoute === item.route ? (
                      <tr className="bg-blue-50">
                        <td colSpan={5} className="p-4">
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <h3 className="font-semibold text-slate-900">
                                Editing: {item.route}
                              </h3>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={handleCancelEdit}
                                >
                                  <X className="w-4 h-4 mr-1" />
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleSave(item.route)}
                                  disabled={saving}
                                  className="bg-trust-blue hover:bg-trust-blue-hover text-white"
                                >
                                  <Save className="w-4 h-4 mr-1" />
                                  {saving ? 'Saving...' : 'Save'}
                                </Button>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <Label className="text-slate-700">Title</Label>
                                <Input
                                  value={editForm.title}
                                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                                  placeholder="Page title"
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-slate-700">Canonical URL</Label>
                                <Input
                                  value={editForm.canonical_url}
                                  onChange={(e) => setEditForm({ ...editForm, canonical_url: e.target.value })}
                                  placeholder="https://staypairhomes.com/..."
                                  className="mt-1"
                                />
                              </div>
                            </div>
                            
                            <div>
                              <Label className="text-slate-700">Meta Description</Label>
                              <textarea
                                value={editForm.meta_description}
                                onChange={(e) => setEditForm({ ...editForm, meta_description: e.target.value })}
                                placeholder="Brief description for search results (150-160 chars)"
                                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm resize-none"
                                rows={2}
                              />
                              <p className="text-xs text-slate-500 mt-1">
                                {editForm.meta_description?.length || 0}/160 characters
                              </p>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <Label className="text-slate-700">OG Title</Label>
                                <Input
                                  value={editForm.og_title}
                                  onChange={(e) => setEditForm({ ...editForm, og_title: e.target.value })}
                                  placeholder="Social media title"
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-slate-700">OG Image URL</Label>
                                <Input
                                  value={editForm.og_image_url}
                                  onChange={(e) => setEditForm({ ...editForm, og_image_url: e.target.value })}
                                  placeholder="/staypairhomes-logo.png"
                                  className="mt-1"
                                />
                              </div>
                            </div>
                            
                            <div>
                              <Label className="text-slate-700">OG Description</Label>
                              <textarea
                                value={editForm.og_description}
                                onChange={(e) => setEditForm({ ...editForm, og_description: e.target.value })}
                                placeholder="Description for social media previews"
                                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm resize-none"
                                rows={2}
                              />
                            </div>
                            
                            <div>
                              <Label className="text-slate-700">Keywords</Label>
                              <Input
                                value={editForm.keywords}
                                onChange={(e) => setEditForm({ ...editForm, keywords: e.target.value })}
                                placeholder="comma, separated, keywords"
                                className="mt-1"
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr className="hover:bg-slate-50" data-testid={`metadata-row-${item.route}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4 text-slate-400" />
                            <code className="text-sm font-mono bg-slate-100 px-2 py-0.5 rounded">
                              {item.route}
                            </code>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-700 line-clamp-1">
                            {item.title || <span className="text-slate-400 italic">Not set</span>}
                          </span>
                        </td>
                        <td className="px-4 py-3 max-w-xs">
                          <span className="text-sm text-slate-500 line-clamp-1">
                            {item.meta_description || <span className="text-slate-400 italic">Not set</span>}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {item.title && item.meta_description ? (
                            <Badge className="bg-green-100 text-green-700 border-0">
                              Complete
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700 border-0">
                              Incomplete
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEdit(item)}
                              data-testid={`edit-metadata-${item.route}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              asChild
                            >
                              <a 
                                href={item.route} 
                                target="_blank" 
                                rel="noopener noreferrer"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDelete(item.route)}
                              data-testid={`delete-metadata-${item.route}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Route Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Add New Route</h3>
            <div className="space-y-4">
              <div>
                <Label className="text-slate-700">Route Path</Label>
                <Input
                  value={newRouteForm.route}
                  onChange={(e) => setNewRouteForm({ route: e.target.value })}
                  placeholder="/new-page"
                  className="mt-1"
                  data-testid="new-route-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Must start with /
                </p>
              </div>
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAddModal(false);
                    setNewRouteForm({ route: '' });
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddRoute}
                  disabled={saving}
                  className="bg-trust-blue hover:bg-trust-blue-hover text-white"
                  data-testid="create-route-btn"
                >
                  {saving ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMetadataPage;
